import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { ExpandMore, AttachFile, InfoOutlined } from '@mui/icons-material';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { TreeViewBaseItem } from '@mui/x-tree-view/models';
import Navigation from '../Shared/Navigation.tsx';
import { useTranslation } from '@/core/i18n/i18n';
import PlanAnualIcon from '@/static/icons/Plan-anual.svg?url';
import { useAnyLoading } from '@/core/useAnyLoading';
import {
  CreatePlanInput,
  ExternalDataDTO,
  ExternalHierarchicalModel,
  PLAN_STAGES,
  FocusRequirementWithDetails,
  PlanRequirementDecision,
  PlanRequirementDecisionInput,
  AnnualPlanStep0Context,
  Step0RequirementActivityLink,
  Step0LinkedRequirementActivity,
} from '@shared/schema';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildPlanRequirementDecisionLookup } from '@/core/helpers/planRequirements';

interface TreeNode {
  id: string;
  name: string;
  children?: TreeNode[];
  isExpanded?: boolean;
  isLeaf?: boolean;
  hierarchyId?: number;
}

interface NewAnnualPlanProps {
  externalData?: ExternalDataDTO | null;
  currentVersion?: number | null;
  createAnnualPlan: (data: CreatePlanInput) => Promise<any>;
  updateAnnualPlan?: (
    planId: number,
    data: { Year?: number; HierarchicalModelId?: number },
  ) => Promise<any>;
  getExternalData: () => void;
  setCurrentVersion: (versionId: number | null) => void;
  setApYear: (year: number | null) => void;
  scopeName: string;
  currentPlanId?: number | null;
  currentBusinessUnit?: string | null;
  selectedHierarchicalModelId?: number | null;
  setCurrentPlanId?: (planId: number | null) => void;
  setSelectedHierarchicalModel?: (hierarchicalModelId: number | null) => void;
  selectedYear?: number | null;
  creatingOutOfTimePlan: boolean;
  outOfTimeRequestId: number | null;
  setCreatingOutOfTimePlan: (creatingOutOfTime: boolean, requestId?: number | null) => void;
  requirements: FocusRequirementWithDetails[];
  savePlanRequirementDecisions: (
    planId: number,
    decisions: PlanRequirementDecisionInput[],
  ) => Promise<void>;
  fetchAnnualPlanStep0Context: (params: {
    planId?: number;
    versionId?: number;
    year?: number;
  }) => Promise<AnnualPlanStep0Context | null>;
  uploadPlanRequirementEvidence?: (file: File) => Promise<{ name: string; url: string } | null>;
  planRequirementDecisions: PlanRequirementDecision[];
  readOnlyMode: boolean | null;
}

type RequirementEvidenceState = {
  name: string;
  url: string | null;
};

type RequirementResponseState = {
  applies: '' | 'yes' | 'no';
  comment: string;
  evidence: RequirementEvidenceState | null;
};

type ChecklistResponsesState = Record<number, Record<number, RequirementResponseState>>;

type FocusChecklistItem = {
  focus: { APFocusId: number; Name: string };
  requirementGroups: FocusRequirementWithDetails['RequirementTypes'];
};

const EVIDENCE_ACCEPTED_FILE_TYPES =
  'image/*,.pdf,.xls,.xlsx,.csv,.doc,.docx,.ppt,.pptx,.heic,.heif,.zip,.rar';

const EMPTY_REQUIREMENT_RESPONSE: RequirementResponseState = {
  applies: 'yes',
  comment: '',
  evidence: null,
};

const NewAnnualPlan: React.FC<NewAnnualPlanProps> = ({
  externalData,
  createAnnualPlan,
  currentVersion,
  getExternalData,
  setCurrentVersion,
  setApYear,
  scopeName,
  currentPlanId,
  currentBusinessUnit,
  selectedHierarchicalModelId,
  updateAnnualPlan,
  setCurrentPlanId,
  setSelectedHierarchicalModel,
  selectedYear: selectedYearFromStore,
  creatingOutOfTimePlan,
  setCreatingOutOfTimePlan,
  outOfTimeRequestId,
  requirements = [],
  savePlanRequirementDecisions,
  fetchAnnualPlanStep0Context,
  uploadPlanRequirementEvidence,
  planRequirementDecisions,
  readOnlyMode,
}) => {
  const isAnyLoading = useAnyLoading();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 1 }, (_, i) => currentYear + i);
  const [selectedYear, setSelectedYear] = useState<string>(
    (years[0] ?? new Date().getFullYear()).toString(),
  );
  const [selectedHierarchyId, setSelectedHierarchyId] = useState<number>(
    selectedHierarchicalModelId || 0,
  );
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [checklistResponses, setChecklistResponses] = useState<ChecklistResponsesState>({});
  const [expandedFocusId, setExpandedFocusId] = useState<number | false>(false);
  const [focusNameMap, setFocusNameMap] = useState<Record<number, string>>({});
  const [decisionsInitialized, setDecisionsInitialized] = useState(false);
  const [hasFetchedDecisions, setHasFetchedDecisions] = useState(false);
  const [requirementActivitiesMap, setRequirementActivitiesMap] = useState<
    Record<string, Step0LinkedRequirementActivity[]>
  >({});
  const [blockedRequirementWarnings, setBlockedRequirementWarnings] = useState<
    Record<string, boolean>
  >({});
  const [infoModalRequirementKey, setInfoModalRequirementKey] = useState<string | null>(null);
  const decisionsForCurrentPlan = React.useMemo(
    () =>
      (planRequirementDecisions ?? []).filter((decision) =>
        currentPlanId ? decision.APId === currentPlanId : true,
      ),
    [planRequirementDecisions, currentPlanId],
  );
  useEffect(() => {
    setDecisionsInitialized(false);
    setHasFetchedDecisions(false);
  }, [currentPlanId]);

  useEffect(() => {
    if (creatingOutOfTimePlan) {
      if (setCurrentPlanId) setCurrentPlanId(null);
      setCurrentVersion(null);
      if (setSelectedHierarchicalModel) setSelectedHierarchicalModel(null);
    }
  }, [creatingOutOfTimePlan, setCurrentPlanId, setCurrentVersion, setSelectedHierarchicalModel]);

  const buildOrganizationTree = React.useCallback((): TreeNode => {
    if (!externalData?.hierarchicalModels || externalData.hierarchicalModels.length === 0) {
      return {
        id: 'root',
        name: 'Company',
        isExpanded: true,
        children: [],
      };
    }

    const buildNode = (model: ExternalHierarchicalModel): TreeNode => {
      const children = externalData.hierarchicalModels.filter(
        (m: ExternalHierarchicalModel) => m.reportsTo === model.id,
      );

      const node: TreeNode = {
        id: `hierarchy-${model.id}`,
        name: model.designation,
        isExpanded: true,
        hierarchyId: model.id,
        isLeaf: children.length === 0,
      };

      if (children.length > 0) {
        node.children = children.map((child) => buildNode(child));
      }

      return node;
    };

    const rootModels = externalData.hierarchicalModels.filter(
      (model: ExternalHierarchicalModel) => model.reportsTo === 0 || model.reportsTo === null,
    );

    const rootNode: TreeNode = {
      id: 'root',
      name: 'Company',
      isExpanded: true,
      children: rootModels.map((model) => buildNode(model)),
    };

    return rootNode;
  }, [externalData]);

  const organizationTree = React.useMemo(() => buildOrganizationTree(), [buildOrganizationTree]);

  const transformToTreeViewItem = React.useCallback(function transformToTreeViewItem(
    node: TreeNode,
  ): TreeViewBaseItem {
    const item: TreeViewBaseItem = { id: node.id, label: node.name };
    if (node.children && node.children.length > 0) {
      item.children = node.children.map((child) => transformToTreeViewItem(child));
    }
    return item;
  }, []);

  const treeViewItems: TreeViewBaseItem[] = React.useMemo(
    () => (organizationTree.children ? organizationTree.children.map(transformToTreeViewItem) : []),
    [organizationTree, transformToTreeViewItem],
  );

  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const collectAllIds = React.useCallback((items: TreeViewBaseItem[]): string[] => {
    const ids: string[] = [];
    const walk = (arr: TreeViewBaseItem[]) => {
      for (const it of arr) {
        ids.push(it.id as string);
        if (it.children && it.children.length) walk(it.children as TreeViewBaseItem[]);
      }
    };
    walk(items);
    return ids;
  }, []);

  useEffect(() => {
    if (treeViewItems.length) {
      const allIds = collectAllIds(treeViewItems);
      setExpandedItems((prev) => {
        if (prev.length === allIds.length && prev.every((id) => allIds.includes(id))) {
          return prev;
        }
        return allIds;
      });
    }
  }, [treeViewItems, collectAllIds]);

  const getHierarchyIdFromNodeId = (nodeId: string): number => {
    const findHierarchyId = (node: TreeNode): number | null => {
      if (node.id === nodeId) {
        return node.hierarchyId || 0;
      }
      if (node.children) {
        for (const child of node.children) {
          const result = findHierarchyId(child);
          if (result !== null) return result;
        }
      }
      return null;
    };
    return findHierarchyId(organizationTree) || 0;
  };

  useEffect(() => {
    if (getExternalData && !externalData) {
      getExternalData();
    }
  }, [externalData, getExternalData]);

  useEffect(() => {
    let cancelled = false;
    const parsedYear = Number.parseInt(selectedYear, 10);
    const year = Number.isInteger(parsedYear) ? parsedYear : undefined;
    if (!year && !currentPlanId) {
      return;
    }

    setHasFetchedDecisions(false);
    fetchAnnualPlanStep0Context({
      ...(currentPlanId ? { planId: currentPlanId } : {}),
      ...(currentVersion ? { versionId: currentVersion } : {}),
      ...(year ? { year } : {}),
    })
      .then((context) => {
        if (cancelled) return;
        setHasFetchedDecisions(true);
        if (!context) {
          setRequirementActivitiesMap({});
          return;
        }

        setFocusNameMap((prev) => {
          const next = { ...prev };
          context.focusNames.forEach((item) => {
            if (item.APFocusId && item.Name) {
              next[item.APFocusId] = item.Name;
            }
          });
          return next;
        });

        const grouped = context.requirementActivityLinks.reduce<
          Record<string, Step0LinkedRequirementActivity[]>
        >((acc, link: Step0RequirementActivityLink) => {
          acc[`${link.focusId}:${link.requirementId}`] = link.activities;
          return acc;
        }, {});
        setRequirementActivitiesMap(grouped);
        setBlockedRequirementWarnings((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((key) => {
            if (!grouped[key]?.length) {
              delete next[key];
            }
          });
          return next;
        });
        setInfoModalRequirementKey((prev) => {
          if (prev === null) return prev;
          if (!grouped[prev] || grouped[prev]!.length === 0) {
            return null;
          }
          return prev;
        });
      })
      .catch((error) => {
        console.error('Error fetching annual plan step 0 context:', error);
        if (!cancelled) {
          setHasFetchedDecisions(true);
          setRequirementActivitiesMap({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentPlanId, currentVersion, selectedYear, fetchAnnualPlanStep0Context]);

  useEffect(() => {
    if (decisionsInitialized || !hasFetchedDecisions) {
      return;
    }
    if (!requirements || requirements.length === 0) {
      setDecisionsInitialized(true);
      return;
    }
    if (!decisionsForCurrentPlan || decisionsForCurrentPlan.length === 0) {
      setDecisionsInitialized(true);
      return;
    }
    const lookup = buildPlanRequirementDecisionLookup(decisionsForCurrentPlan);
    const next: ChecklistResponsesState = {};
    requirements.forEach((focusRequirement) => {
      const focusResponses: Record<number, RequirementResponseState> = {};
      const focusDecisions = lookup.get(focusRequirement.APFocusId);
      focusRequirement.RequirementTypes.forEach((group) => {
        group.requirements.forEach((requirement) => {
          const decision = focusDecisions?.get(requirement.APRequirementId);
          if (!decision) {
            focusResponses[requirement.APRequirementId] = { ...EMPTY_REQUIREMENT_RESPONSE };
            return;
          }
          if (decision.Enabled) {
            focusResponses[requirement.APRequirementId] = { ...EMPTY_REQUIREMENT_RESPONSE };
            return;
          }
          focusResponses[requirement.APRequirementId] = {
            applies: 'no',
            comment: decision.Comment ?? '',
            evidence: decision.EvidenceName
              ? { name: decision.EvidenceName, url: decision.EvidenceUrl }
              : null,
          };
        });
      });
      if (Object.keys(focusResponses).length) {
        next[focusRequirement.APFocusId] = focusResponses;
      }
    });
    if (Object.keys(next).length) {
      setChecklistResponses((prev) => ({ ...prev, ...next }));
    }
    setDecisionsInitialized(true);
  }, [decisionsInitialized, hasFetchedDecisions, decisionsForCurrentPlan, requirements]);

  useEffect(() => {
    if (!requirements || requirements.length === 0) {
      setChecklistResponses({});
      return;
    }
    // Solo inicializar nuevos requirements, preservar los existentes
    setChecklistResponses((prev) => {
      const next: ChecklistResponsesState = { ...prev };
      requirements.forEach((focusRequirement) => {
        if (!next[focusRequirement.APFocusId]) {
          next[focusRequirement.APFocusId] = {};
        }
        const focusState = next[focusRequirement.APFocusId]!;
        focusRequirement.RequirementTypes.forEach((requirementGroup) => {
          requirementGroup.requirements.forEach((requirement) => {
            if (!focusState[requirement.APRequirementId]) {
              focusState[requirement.APRequirementId] = { ...EMPTY_REQUIREMENT_RESPONSE };
            }
          });
        });
      });
      return next;
    });
  }, [requirements]);

  const infoModalActivities = React.useMemo(
    () =>
      infoModalRequirementKey !== null
        ? requirementActivitiesMap[infoModalRequirementKey] || []
        : [],
    [infoModalRequirementKey, requirementActivitiesMap],
  );

  const focusChecklistData = React.useMemo<FocusChecklistItem[]>(() => {
    const requirementMap = new Map<number, FocusRequirementWithDetails>();
    requirements.forEach((req) => {
      requirementMap.set(req.APFocusId, req);
    });
    const sortRequirementGroups = (groups: FocusRequirementWithDetails['RequirementTypes']) =>
      [...groups].sort((a, b) => {
        const aRequired = a?.requirementType?.isRequired ? 0 : 1;
        const bRequired = b?.requirementType?.isRequired ? 0 : 1;
        if (aRequired !== bRequired) return aRequired - bRequired;
        return 0;
      });
    const filteredRequirements = requirements.filter((req) => focusNameMap[req.APFocusId]);
    return filteredRequirements.map((req) => {
      const focusName = focusNameMap[req.APFocusId] ?? `Focus ${req.APFocusId}`;
      const requiredGroups = req.RequirementTypes.filter(
        (group) => group?.requirementType?.isRequired,
      );

      return {
        focus: {
          APFocusId: req.APFocusId,
          Name: focusName,
        },
        requirementGroups: sortRequirementGroups(requiredGroups),
      };
    });
  }, [requirements, focusNameMap, t]);

  const focusChecklistKey = React.useMemo(
    () => focusChecklistData.map((item) => item.focus.APFocusId).join('|'),
    [focusChecklistData],
  );
  const hasInitializedExpansionRef = React.useRef(false);
  const lastFocusChecklistKeyRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (lastFocusChecklistKeyRef.current !== focusChecklistKey) {
      lastFocusChecklistKeyRef.current = focusChecklistKey;
      hasInitializedExpansionRef.current = false;
    }
    if (focusChecklistData.length === 0) {
      setExpandedFocusId(false);
      hasInitializedExpansionRef.current = false;
      return;
    }
    const stillExists = focusChecklistData.some((item) => item.focus.APFocusId === expandedFocusId);
    if (stillExists) {
      return;
    }
    if (expandedFocusId === false && hasInitializedExpansionRef.current) {
      return;
    }
    setExpandedFocusId(focusChecklistData[0]?.focus.APFocusId ?? false);
    hasInitializedExpansionRef.current = true;
  }, [focusChecklistData, focusChecklistKey, expandedFocusId]);

  const handleRequirementApplyChange = React.useCallback(
    (focusId: number, requirementId: number, applies: 'yes' | 'no') => {
      const focusRequirementKey = `${focusId}:${requirementId}`;
      const associatedActivities = requirementActivitiesMap[focusRequirementKey] || [];
      if (applies === 'no' && associatedActivities.length > 0) {
        setBlockedRequirementWarnings((prev) => {
          if (prev[focusRequirementKey]) return prev;
          return { ...prev, [focusRequirementKey]: true };
        });
        return;
      }
      setBlockedRequirementWarnings((prev) => {
        if (!prev[focusRequirementKey]) return prev;
        const next = { ...prev };
        delete next[focusRequirementKey];
        return next;
      });
      setChecklistResponses((prev) => {
        const focusState = { ...(prev[focusId] || {}) };
        const previous = focusState[requirementId] ?? EMPTY_REQUIREMENT_RESPONSE;
        focusState[requirementId] = {
          applies,
          comment: applies === 'no' ? previous.comment : '',
          evidence: applies === 'no' ? previous.evidence : null,
        };
        return {
          ...prev,
          [focusId]: focusState,
        };
      });
    },
    [requirementActivitiesMap],
  );

  const handleRequirementCommentChange = React.useCallback(
    (focusId: number, requirementId: number, value: string) => {
      setChecklistResponses((prev) => {
        const focusState = { ...(prev[focusId] || {}) };
        const previous = focusState[requirementId] ?? EMPTY_REQUIREMENT_RESPONSE;
        focusState[requirementId] = {
          ...previous,
          comment: value,
        };
        return {
          ...prev,
          [focusId]: focusState,
        };
      });
    },
    [],
  );

  const handleRequirementEvidenceUpload = React.useCallback(
    async (focusId: number, requirementId: number, files: FileList | File[] | null) => {
      if (!files || files.length === 0) {
        return;
      }
      const file = files[0];
      if (!file) {
        return;
      }

      let uploaded: { name: string; url: string | null } | null = null;
      if (uploadPlanRequirementEvidence) {
        try {
          const result = await uploadPlanRequirementEvidence(file);
          if (result) {
            uploaded = {
              name: result.name,
              url: result.url,
            };
          }
        } catch (error) {
          console.error('Error uploading requirement evidence', error);
        }
      }

      if (!uploaded) {
        // If upload failed or service not available, do not update state.
        return;
      }

      setChecklistResponses((prev) => {
        const focusState = { ...(prev[focusId] || {}) };
        const previous = focusState[requirementId] ?? EMPTY_REQUIREMENT_RESPONSE;
        focusState[requirementId] = {
          ...previous,
          evidence: uploaded,
        };
        return {
          ...prev,
          [focusId]: focusState,
        };
      });
    },
    [uploadPlanRequirementEvidence],
  );

  const handleRequirementEvidenceDelete = React.useCallback(
    (focusId: number, requirementId: number) => {
      setChecklistResponses((prev) => {
        const focusState = { ...(prev[focusId] || {}) };
        const previous = focusState[requirementId] ?? EMPTY_REQUIREMENT_RESPONSE;
        focusState[requirementId] = {
          ...previous,
          evidence: null,
        };
        return {
          ...prev,
          [focusId]: focusState,
        };
      });
    },
    [],
  );

  useEffect(() => {
    if (currentPlanId && selectedHierarchyId) {
      const nodeId = `hierarchy-${selectedHierarchyId}`;
      setCheckedItems([nodeId]);
    } else if (externalData && externalData.hierarchicalModels?.length && !selectedHierarchyId) {
      const leafModels = externalData.hierarchicalModels.filter(
        (model) => !externalData.hierarchicalModels.some((m) => m.reportsTo === model.id),
      );
      if (leafModels.length > 0) {
        const firstLeaf = leafModels[0];
        if (!firstLeaf) return;
        const nodeId = `hierarchy-${firstLeaf.id}`;
        setSelectedHierarchyId(firstLeaf.id);
        setCheckedItems([nodeId]);
      }
    }
    if (currentPlanId && typeof selectedYearFromStore === 'number') {
      setSelectedYear(String(selectedYearFromStore));
    }
  }, [externalData, selectedHierarchyId, currentPlanId, selectedYearFromStore]);

  const handleItemSelectionToggle = (_event: React.SyntheticEvent, itemId: string) => {
    setCheckedItems([itemId]);
    const hierarchyId = getHierarchyIdFromNodeId(itemId);
    setSelectedHierarchyId(hierarchyId);
  };

  const handleExit = () => {
    setCreatingOutOfTimePlan(false);
    navigate('/');
  };

  const buildPlanRequirementDecisions = React.useCallback(
    (planId: number): PlanRequirementDecisionInput[] => {
      if (!planId) return [];
      const payload: PlanRequirementDecisionInput[] = [];
      //filter only requirements that are in the current requirements list
      const filteredRequirements = requirements.filter((req) => focusNameMap[req.APFocusId]);
      filteredRequirements.forEach((focusRequirement) => {
        const focusState = checklistResponses[focusRequirement.APFocusId] || {};
        focusRequirement.RequirementTypes.forEach((requirementGroup) => {
          requirementGroup.requirements.forEach((requirement) => {
            const response = focusState[requirement.APRequirementId] ?? EMPTY_REQUIREMENT_RESPONSE;
            const applies = response.applies !== 'no';
            const trimmedComment = response.comment.trim();
            payload.push({
              APId: planId,
              APFocusId: focusRequirement.APFocusId,
              APRequirementId: requirement.APRequirementId,
              Enabled: applies,
              Comment: applies ? null : trimmedComment || null,
              EvidenceName: applies ? null : (response.evidence?.name ?? null),
            });
          });
        });
      });
      return payload;
    },
    [requirements, checklistResponses, focusNameMap],
  );

  const handleNext = async () => {
    if (readOnlyMode) {
      const returnTo =
        typeof (location.state as { returnTo?: unknown } | null)?.returnTo === 'string'
          ? ((location.state as { returnTo?: string }).returnTo as string)
          : null;
      const safeReturnTo = returnTo && returnTo.startsWith('/') ? returnTo : null;
      navigate(safeReturnTo ?? '/define-objectives');
      return;
    }
    if (!selectedYear) {
      return;
    }
    if (!selectedHierarchyId) {
      return;
    }
    const yearNumber = parseInt(selectedYear, 10);
    if (currentPlanId && updateAnnualPlan) {
      const planId = currentPlanId;
      const originalYear = typeof selectedYearFromStore === 'number' ? selectedYearFromStore : null;
      const originalHM = selectedHierarchicalModelId || null;
      const hasChanges =
        (originalYear !== null && yearNumber !== originalYear) ||
        (originalHM !== null && selectedHierarchyId !== originalHM);

      try {
        if (hasChanges) {
          await updateAnnualPlan(planId, {
            Year: yearNumber,
            HierarchicalModelId: selectedHierarchyId,
          });
        }

        const decisionsPayload = buildPlanRequirementDecisions(planId);
        await savePlanRequirementDecisions(planId, decisionsPayload);

        setApYear(yearNumber);
        setCreatingOutOfTimePlan(false);
        navigate('/define-objectives');
      } catch (e) {
        console.error('Error updating annual plan or saving requirement decisions', e);
      }
      return;
    }
    if (createAnnualPlan) {
      try {
        const result = await createAnnualPlan({
          Year: yearNumber,
          RiskMatrixScopeId: 0,
          HierarchicalModelId: selectedHierarchyId,
          Status: creatingOutOfTimePlan
            ? PLAN_STAGES.DEFINITION_OUT_OF_TIME
            : PLAN_STAGES.DEFINITION,
          OutOfTimeRequestId: outOfTimeRequestId,
        });
        if (result?.APVersionId) {
          if (result?.APId) {
            const decisionsPayload = buildPlanRequirementDecisions(result.APId);
            await savePlanRequirementDecisions(result.APId, decisionsPayload);
          }
          setApYear(yearNumber);
          setCurrentVersion(result.APVersionId);
          if (setCurrentPlanId && result.APId) setCurrentPlanId(result.APId);
          if (setSelectedHierarchicalModel) setSelectedHierarchicalModel(selectedHierarchyId);
          setCreatingOutOfTimePlan(false);
          navigate('/define-objectives');
        }
      } catch (error) {
        console.error('Error creating annual plan or saving requirement decisions:', error);
      }
    }
  };

  const checklistSummary = React.useMemo(() => {
    let hasNoApply = false;
    let hasIncompleteJustification = false;
    Object.values(checklistResponses).forEach((focusResponses) => {
      Object.values(focusResponses).forEach((response) => {
        if (response.applies === 'no') {
          hasNoApply = true;
          if (response.comment.trim() === '' || !response.evidence) {
            hasIncompleteJustification = true;
          }
        }
      });
    });
    return { hasNoApply, hasIncompleteJustification };
  }, [checklistResponses]);

  const canContinue = selectedYear !== '' && selectedHierarchyId !== 0;
  const disableNext =
    !canContinue || (checklistSummary.hasNoApply && checklistSummary.hasIncompleteJustification);
  const disableNextReason = !canContinue
    ? t('newAnnualPlan.disableNextReason')
    : checklistSummary.hasNoApply && checklistSummary.hasIncompleteJustification
      ? t('newAnnualPlan.requirementsChecklistIncompleteReason')
      : undefined;
  const navigationDisableProps = React.useMemo(
    () => (disableNextReason ? { disableReason: disableNextReason } : {}),
    [disableNextReason],
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef6f6', pt: 4 }}>
      {/* Content */}
      <Box sx={{ p: 3, maxWidth: '1000px', mx: 'auto' }}>
        <Paper sx={{ borderRadius: 1, boxShadow: 2, overflow: 'hidden' }}>
          {/* Header */}
          <Grid
            container
            sx={{
              pt: 2,
              paddingLeft: 3,
              paddingRight: 3,

              bgcolor: '#fafafa',
              borderBottom: '1px solid #A5B2BB',
            }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid size="auto">
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* Bloque Plan Anual */}

                {/* Bloque Ámbito */}
                <Grid
                  size="auto"
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('newAnnualPlan.scope')} {scopeName}
                    {currentBusinessUnit
                      ? ` · ${t('newAnnualPlan.businessUnit')}: ${currentBusinessUnit}`
                      : ''}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {t('newAnnualPlan.annualPlanDefinition')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid size="auto">
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src={PlanAnualIcon} alt="Plan Anual" style={{ width: 70, height: 70 }} />
              </Box>
            </Grid>
          </Grid>

          {/* Organigrama Card */}
          <Box sx={{ p: 3, bgcolor: 'white' }}>
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                color: 'white',
                p: 2,
                borderRadius: 1,
                border: '1px solid #A5B2BB',
              }}
            >
              <Typography
                variant="body2"
                sx={{ mb: 2, fontWeight: 500, color: '#666', fontSize: '0.875rem' }}
              >
                {t('newAnnualPlan.organizationalChart')}
              </Typography>
              <Box
                sx={{
                  minHeight: 300,
                  maxHeight: '60vh',
                  overflow: 'auto',
                  pr: 1,
                }}
              >
                <RichTreeView
                  key={currentPlanId ? `edit-${currentPlanId}` : 'new-plan'}
                  items={treeViewItems}
                  disableSelection={readOnlyMode || isAnyLoading}
                  checkboxSelection
                  multiSelect={false}
                  selectedItems={checkedItems[0] ?? ''}
                  onItemSelectionToggle={(event, itemId) =>
                    handleItemSelectionToggle(event ?? ({} as React.SyntheticEvent), itemId)
                  }
                  expandedItems={expandedItems}
                  onExpandedItemsChange={(_e, itemIds) => setExpandedItems(itemIds as string[])}
                  sx={{
                    color: 'black',
                    '& .MuiTreeItem-label': { color: 'black' },
                  }}
                />
              </Box>
            </Paper>
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                fontWeight: 600,
                fontSize: '0.875rem',
                mt: 3,
              }}
            >
              {t('newAnnualPlan.selectYear')}
            </Typography>

            <FormControl fullWidth>
              <Select
                value={selectedYear}
                disabled={readOnlyMode || isAnyLoading}
                onChange={(e) => setSelectedYear(e.target.value)}
                size="medium"
                sx={{
                  bgcolor: 'white',
                  height: 48,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#A5B2BB',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#bdbdbd',
                  },
                }}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ mt: 4 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                {t('newAnnualPlan.requirementsChecklistTitle')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#5f7382', fontSize: '0.875rem' }}>
                {t('newAnnualPlan.requirementsChecklistDescription')}
              </Typography>
              {focusChecklistData.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 1,
                    border: '1px dashed #a5b2bb',
                    bgcolor: '#fafafa',
                    p: 3,
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#5f7382' }}>
                    {currentVersion
                      ? t('newAnnualPlan.requirementsChecklistNoData')
                      : t('newAnnualPlan.requirementsChecklistRequiresPlan')}
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={2}>
                  {focusChecklistData.map(({ focus, requirementGroups }) => {
                    const focusHasRequirements = requirementGroups.length > 0;
                    return (
                      <Accordion
                        key={focus.APFocusId}
                        disableGutters
                        expanded={expandedFocusId === focus.APFocusId}
                        onChange={(_event, isExpanded) =>
                          setExpandedFocusId(isExpanded ? focus.APFocusId : false)
                        }
                        sx={{
                          border: '1px solid #d5e2e7',
                          borderRadius: 1,
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                          overflow: 'hidden',
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore />}
                          sx={{
                            bgcolor: '#f4f8fa',
                            '& .MuiAccordionSummary-content': {
                              alignItems: 'center',
                              my: 0,
                            },
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {focus.Name}
                          </Typography>
                        </AccordionSummary>
                        {expandedFocusId === focus.APFocusId && (
                          <AccordionDetails sx={{ bgcolor: 'white' }}>
                            {!focusHasRequirements ? (
                              <Typography variant="body2" sx={{ color: '#62727b' }}>
                                {t('newAnnualPlan.requirementsChecklistNoRequirements')}
                              </Typography>
                            ) : (
                              requirementGroups.map((group) => (
                                <Box
                                  key={
                                    group.requirementType.APRequirementTypeId ??
                                    group.requirementType.Name
                                  }
                                  sx={{ mb: 3, '&:last-of-type': { mb: 0 } }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                    {group.requirementType.Name}
                                  </Typography>
                                  <Stack spacing={2}>
                                    {group.requirements.map((requirement) => {
                                      const response = checklistResponses[focus.APFocusId]?.[
                                        requirement.APRequirementId
                                      ] ?? { ...EMPTY_REQUIREMENT_RESPONSE };
                                      const showAdditionalFields = response.applies === 'no';
                                      const commentError =
                                        showAdditionalFields && response.comment.trim() === '';
                                      const evidence = response.evidence;
                                      const evidenceError = showAdditionalFields && !evidence;
                                      const focusRequirementKey = `${focus.APFocusId}:${requirement.APRequirementId}`;
                                      const associatedActivities =
                                        requirementActivitiesMap[focusRequirementKey] || [];
                                      const hasAssociatedActivities =
                                        associatedActivities.length > 0;
                                      const isBlocked =
                                        hasAssociatedActivities &&
                                        Boolean(blockedRequirementWarnings[focusRequirementKey]);
                                      return (
                                        <Paper
                                          key={requirement.APRequirementId}
                                          elevation={0}
                                          sx={{
                                            border: '1px solid #d5e2e7',
                                            borderRadius: 1,
                                            p: 2,
                                            bgcolor: 'white',
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, mb: 1 }}
                                          >
                                            {requirement.Name}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{ color: '#62727b', mb: 1 }}
                                          >
                                            {t('newAnnualPlan.requirementsChecklistApplyQuestion')}
                                          </Typography>
                                          <RadioGroup
                                            row
                                            value={response.applies}
                                            onChange={(event) =>
                                              handleRequirementApplyChange(
                                                focus.APFocusId,
                                                requirement.APRequirementId,
                                                event.target.value as 'yes' | 'no',
                                              )
                                            }
                                            sx={{
                                              '& .MuiFormControlLabel-root': {
                                                mr: 3,
                                              },
                                            }}
                                          >
                                            <FormControlLabel
                                              value="yes"
                                              disabled={!!readOnlyMode || isAnyLoading}
                                              control={<Radio />}
                                              label={t('newAnnualPlan.requirementsChecklistYes')}
                                            />
                                            <FormControlLabel
                                              value="no"
                                              disabled={!!readOnlyMode || isAnyLoading}
                                              control={<Radio />}
                                              label={t('newAnnualPlan.requirementsChecklistNo')}
                                            />
                                          </RadioGroup>
                                          {hasAssociatedActivities && (
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mt: 1,
                                              }}
                                            >
                                              {isBlocked && (
                                                <>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{ color: 'error.main', fontWeight: 500 }}
                                                  >
                                                    {t(
                                                      'newAnnualPlan.requirementsChecklistHasActivitiesError',
                                                    )}
                                                  </Typography>
                                                  <Tooltip
                                                    title={t(
                                                      'newAnnualPlan.requirementsChecklistBlockedInfo',
                                                    )}
                                                  >
                                                    <span>
                                                      <IconButton
                                                        size="small"
                                                        color={isBlocked ? 'error' : 'primary'}
                                                        onClick={() =>
                                                          setInfoModalRequirementKey(
                                                            focusRequirementKey,
                                                          )
                                                        }
                                                      >
                                                        <InfoOutlined fontSize="small" />
                                                      </IconButton>
                                                    </span>
                                                  </Tooltip>
                                                </>
                                              )}
                                            </Box>
                                          )}
                                          {showAdditionalFields && (
                                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                              <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography
                                                  variant="body2"
                                                  sx={{ mb: 0.5, fontWeight: 600 }}
                                                >
                                                  {t(
                                                    'newAnnualPlan.requirementsChecklistCommentLabel',
                                                  )}
                                                </Typography>
                                                <TextField
                                                  fullWidth
                                                  disabled={readOnlyMode || isAnyLoading}
                                                  required
                                                  multiline
                                                  minRows={4}
                                                  maxRows={4}
                                                  placeholder={t(
                                                    'newAnnualPlan.requirementsChecklistCommentPlaceholder',
                                                  )}
                                                  value={response.comment}
                                                  onChange={(event) =>
                                                    handleRequirementCommentChange(
                                                      focus.APFocusId,
                                                      requirement.APRequirementId,
                                                      event.target.value,
                                                    )
                                                  }
                                                  error={commentError}
                                                  helperText={
                                                    commentError
                                                      ? t(
                                                          'newAnnualPlan.requirementsChecklistCommentRequired',
                                                        )
                                                      : ' '
                                                  }
                                                />
                                              </Grid>
                                              <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography
                                                  variant="body2"
                                                  sx={{ mb: 0.5, fontWeight: 600 }}
                                                >
                                                  {t(
                                                    'newAnnualPlan.requirementsChecklistEvidenceLabel',
                                                  )}
                                                </Typography>
                                                {evidence ? (
                                                  <Box
                                                    sx={{
                                                      border: '1px solid #d5e2e7',
                                                      borderRadius: 1,
                                                      p: 1.5,
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      gap: 1,
                                                      bgcolor: '#fafcfd',
                                                    }}
                                                  >
                                                    <Box
                                                      sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        flexWrap: 'wrap',
                                                      }}
                                                    >
                                                      <AttachFile
                                                        fontSize="small"
                                                        color="primary"
                                                      />
                                                      <Typography
                                                        variant="caption"
                                                        sx={{ flex: 1, wordBreak: 'break-all' }}
                                                      >
                                                        {evidence.name}
                                                      </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                      <Button
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ flex: 1, textTransform: 'none' }}
                                                        disabled={isAnyLoading}
                                                        onClick={() =>
                                                          evidence.url &&
                                                          window.open(evidence.url, '_blank')
                                                        }
                                                      >
                                                        {t('common.view')}
                                                      </Button>
                                                      <Button
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                        sx={{ flex: 1, textTransform: 'none' }}
                                                        disabled={readOnlyMode || isAnyLoading}
                                                        onClick={() =>
                                                          handleRequirementEvidenceDelete(
                                                            focus.APFocusId,
                                                            requirement.APRequirementId,
                                                          )
                                                        }
                                                      >
                                                        {t('common.delete')}
                                                      </Button>
                                                    </Box>
                                                  </Box>
                                                ) : (
                                                  <Box
                                                    onDragOver={(event) => event.preventDefault()}
                                                    onDrop={(event) => {
                                                      event.preventDefault();
                                                      if (event.dataTransfer?.files?.length) {
                                                        handleRequirementEvidenceUpload(
                                                          focus.APFocusId,
                                                          requirement.APRequirementId,
                                                          event.dataTransfer.files,
                                                        );
                                                      }
                                                    }}
                                                    sx={{
                                                      border: `1px dashed ${evidenceError ? '#d32f2f' : '#00BCE4'}`,
                                                      borderRadius: 1,
                                                      p: 2,
                                                      textAlign: 'center',
                                                      cursor: 'pointer',
                                                      bgcolor: '#ffffff',
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      gap: 1,
                                                      '&:hover': {
                                                        bgcolor: '#f2fbfd',
                                                      },
                                                    }}
                                                    onClick={() => {
                                                      if (readOnlyMode) {
                                                        return;
                                                      }
                                                      const input = document.createElement('input');
                                                      input.type = 'file';
                                                      input.accept = EVIDENCE_ACCEPTED_FILE_TYPES;
                                                      input.onchange = (ev: Event) => {
                                                        const target =
                                                          ev.target as HTMLInputElement;
                                                        handleRequirementEvidenceUpload(
                                                          focus.APFocusId,
                                                          requirement.APRequirementId,
                                                          target.files,
                                                        );
                                                      };
                                                      input.click();
                                                    }}
                                                  >
                                                    <AttachFile
                                                      sx={{ fontSize: 32, color: '#00BCE4' }}
                                                    />
                                                    <Typography
                                                      variant="caption"
                                                      sx={{ color: '#555', px: 1 }}
                                                    >
                                                      {t(
                                                        'newAnnualPlan.requirementsChecklistEvidenceHint',
                                                      )}
                                                    </Typography>
                                                    <Button
                                                      size="small"
                                                      variant="outlined"
                                                      sx={{
                                                        borderColor: '#00BCE4',
                                                        color: '#00BCE4',
                                                        textTransform: 'none',
                                                        px: 2,
                                                        '&:hover': { borderColor: '#009bbd' },
                                                      }}
                                                    >
                                                      {t('common.select')}
                                                    </Button>
                                                  </Box>
                                                )}
                                                {evidenceError && (
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      color: 'error.main',
                                                      display: 'block',
                                                      mt: 0.5,
                                                    }}
                                                  >
                                                    {t(
                                                      'newAnnualPlan.requirementsChecklistEvidenceRequired',
                                                    )}
                                                  </Typography>
                                                )}
                                              </Grid>
                                            </Grid>
                                          )}
                                        </Paper>
                                      );
                                    })}
                                  </Stack>
                                </Box>
                              ))
                            )}
                          </AccordionDetails>
                        )}
                      </Accordion>
                    );
                  })}
                </Stack>
              )}
            </Box>
            {/* Next Button */}
            {(() => {
              const originalYear =
                typeof selectedYearFromStore === 'number' ? selectedYearFromStore : null;
              const originalHM = selectedHierarchicalModelId || null;
              const yearNumber = parseInt(selectedYear, 10);
              const hasChanges = currentPlanId
                ? (originalYear !== null && yearNumber !== originalYear) ||
                  (originalHM !== null && selectedHierarchyId !== originalHM)
                : true;
              const nextLabel = currentPlanId
                ? hasChanges
                  ? t('common.update')
                  : t('common.continue')
                : t('common.next');
              return (
                <Navigation
                  onNext={handleNext}
                  isAnyLoading={isAnyLoading}
                  nextTitle={nextLabel}
                  onExit={handleExit}
                  exitTitle={t('common.exit')}
                  disableNext={disableNext}
                  readOnlyMode={readOnlyMode}
                  {...navigationDisableProps}
                />
              );
            })()}
          </Box>
        </Paper>
      </Box>
      <Dialog
        open={infoModalRequirementKey !== null}
        onClose={() => setInfoModalRequirementKey(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t('newAnnualPlan.requirementsChecklistBlockedModalTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: '#424242' }}>
            {t('newAnnualPlan.requirementsChecklistBlockedModalDescription')}
          </Typography>
          {infoModalActivities.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#62727b' }}>
              {t('newAnnualPlan.requirementsChecklistNoAssociatedActivities')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {infoModalActivities.map((activity) => (
                <ListItem key={activity.id} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={activity.name}
                    secondary={t('newAnnualPlan.requirementsChecklistActivityObjective', {
                      id: activity.objectiveId,
                    })}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'caption', color: '#607d8b' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoModalRequirementKey(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewAnnualPlan;
